import { hasRole, roleFor } from "@netmountains/shared/browser";
import lazyPage from "utils/lazyPage";

/**
 * The walker's Begehungsprotokoll is switched off for now. Off, the walker menu leaves out its
 * row (nav/config.js) and only superAdmin may open the page, so a walker who opens its address
 * lands on the Dashboard. Drafts, the page's code and the API stay as they are; true brings it
 * back.
 */
export const WALKER_PROTOCOL_ENABLED = false;

/**
 * The dashboard's pages by path (below /dashboard), each with the roles that may open it.
 * `RequireRole` sends any other account to its landing page. superAdmin opens every page, as it
 * passes every gate on the server (`hasRole`). The server checks every call anyway; this keeps
 * an account off screens it can't use. A new page goes here, and into its role's menu in
 * `nav/config.js`.
 */
export const DASHBOARD_PAGES = {
  // Back office
  app: { roles: ["admin"], Page: lazyPage(() => import("pages/Dashboard")) },
  evaluation: { roles: ["admin"], Page: lazyPage(() => import("pages/evaluation/evaluationPage")) },
  // admin's menu row is disabled, but the Dashboard and Präqualifizierung open assessments here
  assessment: {
    roles: ["admin"],
    Page: lazyPage(() => import("pages/AssessmentPage/AssessmentPage")),
  },
  stammdaten: { roles: ["admin"], Page: lazyPage(() => import("pages/baseData")) },
  billing: { roles: ["admin"], Page: lazyPage(() => import("pages/BillingPage")) },
  "major-customer-billing": {
    roles: ["admin"],
    Page: lazyPage(() => import("pages/MajorCustomerBillPage")),
  },
  changelog: { roles: ["admin"], Page: lazyPage(() => import("pages/changelog")) },
  statistics: {
    roles: ["superAdmin"],
    Page: lazyPage(() => import("pages/statistics/statisticsPage")),
  },
  supervisor: {
    roles: ["superAdmin"],
    Page: lazyPage(() => import("pages/SupervisorPage/superVisorPage")),
  },
  // Inspections: staff plan them on the list ("Begehungen"), the walker also has Upload, and
  // Protokoll while it is switched on (WALKER_PROTOCOL_ENABLED)
  walker: { roles: ["admin", "walker"], Page: lazyPage(() => import("pages/walkerPage")) },
  walkerProtocol: {
    roles: WALKER_PROTOCOL_ENABLED ? ["walker"] : ["superAdmin"],
    Page: lazyPage(() => import("pages/walkerProtocol/walkerProtocolPage")),
  },
  walkerUpload: { roles: ["walker"], Page: lazyPage(() => import("pages/walkerUpload")) },
  // Company portal
  company: { roles: ["user"], Page: lazyPage(() => import("pages/Company")) },
  facility: { roles: ["user"], Page: lazyPage(() => import("pages/Facility")) },
  details: { roles: ["user"], Page: lazyPage(() => import("pages/details/Details")) },
  inventoryList: { roles: ["user"], Page: lazyPage(() => import("pages/inventoryList")) },
};

// Where /dashboard sends each role, and where a page the role may not open sends it instead.
// Keyed by the shared roleFor() strings, so the SPA and the API agree on what an account is.
const ROLE_LANDING_ROUTE = {
  superAdmin: "/dashboard/app",
  admin: "/dashboard/app",
  walker: "/dashboard/walker",
  user: "/dashboard/company",
};

/** The page an account starts on. */
export const landingRouteFor = account => ROLE_LANDING_ROUTE[roleFor(account)];

/** Whether the account may open the dashboard page at `path` (below /dashboard). */
export const canOpenPage = (account, path) =>
  Object.prototype.hasOwnProperty.call(DASHBOARD_PAGES, path) &&
  hasRole(account, DASHBOARD_PAGES[path].roles);

/** Loads the code of every page the account may open, in the background. */
export const preloadPages = account => {
  for (const [path, { Page }] of Object.entries(DASHBOARD_PAGES)) {
    if (canOpenPage(account, path)) Page.preload();
  }
};
